import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import {
	Dropdown,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from 'reactstrap';

import { IconButton, formatNumber, tcBuildSessionURL, SCOPES } from '@techchange/components';
import { messages } from '../../../i18n';

import styles from './PointsNavDropdownMenu.module.scss';

const DROPDOWN_MENU_DOM_ID = 'points-nav-item-dropdown-menu';

const propTypes = {

	/**
	 * The point label for the organization.
	 */
	pointLabel: PropTypes.shape({

		/**
		 * The point label id.
		 */
		id: PropTypes.string,

		/**
		 * The singular label.
		 */
		singular_label: PropTypes.string,

		/**
		 * The plural label.
		 */
		plural_label: PropTypes.string,

	}),

	/**
	 * The org-wide point total for the current user and organization.
	 */
	orgPointTotal: PropTypes.shape({

		/**
		 * The value of the point total.
		 */
		total: PropTypes.number,

		/**
		 * The scope of the point total.
		 */
		scope: PropTypes.oneOf([SCOPES.ORGANIZATION]).isRequired,

	}),

	/**
	 * The list of session-by-session point totals for the current user.
	 */
	sessionPointTotals: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The value of the point total.
		 */
		total: PropTypes.number,

		/**
		 * The scope of the point total.
		 */
		scope: PropTypes.oneOf([SCOPES.SESSION]).isRequired,

		/**
		 * The scope ID of the point total.
		 */
		scope_id: PropTypes.string.isRequired,

		/**
		 * The associated session of the point total.
		 */
		session: PropTypes.shape({

			/**
			 * The session ID.
			 */
			id: PropTypes.string.isRequired,

			/**
			 * The session slug.
			 */
			slug: PropTypes.string.isRequired,

			/**
			 * The session title.
			 */
			title: PropTypes.string.isRequired,

		}),

	})),

	/**
	 * True, if there was an error loading the required data for the dropdown menu.
	 */
	showError: PropTypes.bool,

	/**
	 * The state of loading the required data for the dropdown menu.
	 */
	menuLoadState: PropTypes.shape({

		/**
		 * True, if loading data for the dropdown menu.
		 */
		loading: PropTypes.bool,

		/**
		 * True, if done loading data for the dropdown menu.
		 */
		loadedAll: PropTypes.bool,

	}),

	/**
	 * Callback to handle loading required data for the dropdown menu.
	 */
	onLoadMenuData: PropTypes.func.isRequired,

};

const PointsNavDropdownMenu = (props) => {

	const {
		pointLabel,
		orgPointTotal,
		sessionPointTotals,
		showError,
		menuLoadState,
		onLoadMenuData,
	} = props;

	const intl = useIntl();

	// Keep track of the dropdown menu state
	const [menuState, setMenuState] = useState({
		isOpen: false,
		fetchedData: false,
	});

	// Handle toggling the dropdown menu state
	const toggle = () => {

		// Check if we need to fetch the required data for the dropdown menu
		if (!menuState.fetchedData) {
			onLoadMenuData();
		}

		setMenuState({ isOpen: !menuState.isOpen, fetchedData: true });

	};

	// Check if the org-wide point data is loaded
	const hasLoadedPointData = Boolean(orgPointTotal) && Boolean(pointLabel);

	// Keep track of the content to show in the dropdown menu
	let content = <h2 className="text-center"><i className="fas fa-circle-notch fa-spin" /></h2>;

	// Keep track of the user's org-wide point total
	let userPointTotal = 0;

	if (hasLoadedPointData) {

		userPointTotal = orgPointTotal.total;

		// Check if the rest of the required data is loaded for the dropdown menu
		if (sessionPointTotals) {

			// Loop through and display each session-by-session point total for the current user
			content = (
				<Fragment>
					<div className="tc-universal-dropdown-menu-header">
						<div className="tc-universal-dropdown-menu-header-title">
							{pointLabel && pointLabel.plural_label}
						</div>
						<div className="tc-universal-dropdown-menu-header-subtitle">
							<FormattedMessage
								{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_DROPDOWN_MENU_POINTS_SUB_HEADER_TEXT}
								values={{
									total: <strong>{formatNumber(userPointTotal)}</strong>,
									label: userPointTotal === 1 ? pointLabel.singular_label : pointLabel.plural_label
								}} />
						</div>
					</div>
					<DropdownItem divider />
					{sessionPointTotals.map((pointTotal) => {

						const session = pointTotal.session;

						if (!session) {
							return null;
						}

						return (
							<DropdownItem
								key={pointTotal.id}
								className={styles['dropdown-menu-item-session-by-session']}
								tag="a"
								href={tcBuildSessionURL(session)}
								rel="noopener noreferrer"
								target="_blank"
								title={intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_GENERAL_TITLE_TEXT_LINK_TO_COURSE, { title: session.title })}>
								<span className={styles['dropdown-menu-item-session-point-total']}>
									{formatNumber(pointTotal.total)}
								</span>
								<span>
									{session.title}
								</span>
							</DropdownItem>
						);

					})}
					{!menuLoadState.loadedAll &&
						<div
							className={styles['dropdown-menu-item-loading-indicator']}>
							{menuLoadState.loading &&
								<i className="fas fa-circle-notch fa-spin fa-lg" />
							}
						</div>
					}
				</Fragment>
			);

		}

	}

	return (
		<Dropdown
			id="points-nav-item"
			className="d-none d-sm-block"
			isOpen={menuState.isOpen}
			toggle={toggle}
			nav
			inNavbar>
			<DropdownToggle
				className="btn tc-icon-button"
				tag="button"
				title={
					pointLabel?.plural_label ||
					intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_DROPDOWN_MENU_POINTS_TEXT_POINTS_DEFAULT_LABEL_PLURAL)
				}
				nav>
				<IconButton
					// https://github.com/reactstrap/reactstrap/issues/2758
					tag={<span />}
					icon="fal fa-dot-circle fa-xl"
					title={
						pointLabel?.plural_label ||
						intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_DROPDOWN_MENU_POINTS_TEXT_POINTS_DEFAULT_LABEL_PLURAL)
					}
					// countColor="danger"
					// count={userPointTotal}
					// hasCount
					size="45px" />
			</DropdownToggle>
			<DropdownMenu
				id={DROPDOWN_MENU_DOM_ID}
				data-bs-popper="static"
				end>
				{showError &&
					<div className={styles['dropdown-menu-item-errors']}>
						{pointLabel &&
							<FormattedMessage
								{...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_DROPDOWN_MENU_POINTS_TEXT_ERROR_MSG_WITH_LABEL}
								values={{ points_label: pointLabel.plural_label }} />
						}
						{!pointLabel &&
							<FormattedMessage {...messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_DROPDOWN_MENU_POINTS_TEXT_ERROR_MSG_NO_LABEL} />
						}
					</div>
				}
				{!showError && content}
			</DropdownMenu>
		</Dropdown>
	);

};

PointsNavDropdownMenu.propTypes = propTypes;

export default PointsNavDropdownMenu;
